import { useEffect, useMemo, useState } from 'react';
import DOMPurify from 'dompurify';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { accountApi } from '../api/account';
import { catalogApi } from '../api/catalog';
import type { PagedResult, ProductReviewCreateDto, ProductReviewDto, ProductVariantDto } from '../api/types';
import { ProductCard, Stars } from '../components/ProductCard';
import { EmptyState, ErrorState, Field, FormErrors, PageLoader, Pagination, Spinner } from '../components/ui';
import { useToast } from '../components/Toast';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { formatDate, money } from '../lib/format';
import { Check, ThumbsDown, ThumbsUp } from 'lucide-react';
import { Seo } from '../components/Seo';
import { ProductImageGallery } from '../components/ProductImageGallery';
import { trackViewItem } from '../lib/analytics';

const COLOR_MAP: Record<string, string> = {
  siyah: '#18181b',
  black: '#18181b',
  'puma siyahı': '#1f2421',
  'siyah titanyum': '#2e2e2e',
  'doğal titanyum': '#9a9590',
  'mavi titanyum': '#3b4450',
  'beyaz titanyum': '#e5e5e5',
  beyaz: '#ffffff',
  white: '#ffffff',
  kırmızı: '#dc2626',
  red: '#dc2626',
  'ateş kırmızısı': '#dc2626',
  mavi: '#2563eb',
  blue: '#2563eb',
  lacivert: '#1e3a8a',
  navy: '#1e3a8a',
  yeşil: '#16a34a',
  green: '#16a34a',
  haki: '#556b2f',
  sarı: '#eab308',
  yellow: '#eab308',
  turuncu: '#ea580c',
  orange: '#ea580c',
  mor: '#9333ea',
  purple: '#9333ea',
  pembe: '#ec4899',
  pink: '#ec4899',
  gri: '#6b7280',
  gray: '#6b7280',
  grey: '#6b7280',
  gümüş: '#c0c0c0',
  silver: '#c0c0c0',
  altın: '#d4af37',
  gold: '#d4af37',
  kahverengi: '#78350f',
  brown: '#78350f',
  bej: '#f5f5dc',
  beige: '#f5f5dc',
  antrasit: '#334155',
  bordo: '#800020',
};

function cleanColorLabel(value: string | undefined | null): string {
  if (!value) return '';
  if (value.includes('|#')) {
    return value.split('|#')[0].trim();
  }
  if (value.includes('|')) {
    return value.split('|')[0].trim();
  }
  return value.trim();
}

function resolveColor(value: string, colorCode?: string | null): string {
  if (colorCode && colorCode.trim().length > 0) return colorCode.trim();

  if (value.includes('|#')) {
    const parts = value.split('|#');
    return `#${parts[1].trim()}`;
  }
  if (value.includes('|')) {
    const parts = value.split('|');
    if (parts[1].trim().startsWith('#')) return parts[1].trim();
  }

  const normalized = cleanColorLabel(value).toLowerCase();
  if (COLOR_MAP[normalized]) return COLOR_MAP[normalized];
  for (const [key, hex] of Object.entries(COLOR_MAP)) {
    if (normalized.includes(key)) return hex;
  }
  return '#94a3b8';
}

function isLightColor(hex: string): boolean {
  if (!hex.startsWith('#')) return false;
  let clean = hex.slice(1);
  if (clean.length === 3) {
    clean = clean.split('').map((c) => c + c).join('');
  }
  if (clean.length !== 6) return false;
  const r = parseInt(clean.substring(0, 2), 16);
  const g = parseInt(clean.substring(2, 4), 16);
  const b = parseInt(clean.substring(4, 6), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 180;
}

export function ProductDetailPage() {
  const { slug = '' } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const cart = useCart();
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();

  const [selectedOptions, setSelectedOptions] = useState<Record<string, string>>({});
  const [quantity, setQuantity] = useState(1);
  const [reviewPage, setReviewPage] = useState(1);
  const [votingReviewId, setVotingReviewId] = useState<number | null>(null);

  const product = useQuery({
    queryKey: ['product', slug],
    queryFn: () => catalogApi.productBySlug(slug),
    enabled: slug.length > 0,
  });

  const productId = product.data?.id;

  // Analytics: Product view event
  useEffect(() => {
    if (product.data) {
      const categoryName =
        product.data.breadcrumbs && product.data.breadcrumbs.length > 0
          ? product.data.breadcrumbs[product.data.breadcrumbs.length - 1].name
          : undefined;
      trackViewItem({
        id: product.data.id,
        name: product.data.title,
        price: product.data.minPrice || product.data.variants[0]?.price || 0,
        category: categoryName,
        brand: product.data.brandName || undefined,
      });
    }
  }, [product.data]);

  const reviews = useQuery({
    queryKey: ['product', productId, 'reviews', reviewPage],
    queryFn: () => catalogApi.productReviews(productId!, { page: reviewPage, pageSize: 5 }),
    enabled: productId !== undefined,
  });

  const handleVote = async (reviewId: number, helpful: boolean) => {
    if (votingReviewId !== null) return;
    setVotingReviewId(reviewId);
    try {
      const result = await catalogApi.markReviewHelpful(reviewId, helpful);
      queryClient.setQueryData<PagedResult<ProductReviewDto>>(
        ['product', productId, 'reviews', reviewPage],
        (old) => {
          if (!old) return old;
          return {
            ...old,
            items: old.items.map((r) =>
              r.id === reviewId
                ? {
                    ...r,
                    helpfulCount: result.helpfulCount,
                    notHelpfulCount: result.notHelpfulCount,
                    currentUserVote: result.currentUserVote,
                  }
                : r,
            ),
          };
        },
      );

      if (result.currentUserVote === null || result.currentUserVote === undefined) {
        toast.info('Oyunuz geri alındı.');
      } else {
        toast.success('Geri bildiriminiz için teşekkür ederiz.');
      }
    } catch (err) {
      toast.fromError(err, 'İşlem tamamlanamadı.');
    } finally {
      setVotingReviewId(null);
    }
  };

  const summary = useQuery({
    queryKey: ['product', productId, 'review-summary'],
    queryFn: () => catalogApi.reviewSummary(productId!),
    enabled: productId !== undefined,
  });

  const related = useQuery({
    queryKey: ['product', productId, 'related'],
    queryFn: () => catalogApi.relatedProducts(productId!, 4),
    enabled: productId !== undefined,
  });

  /**
   * Secili seceneklere tam uyan varyant. Urunun tek varyanti varsa
   * secim yapilmadan da o varyant kullanilir.
   */
  const selectedVariant = useMemo<ProductVariantDto | null>(() => {
    const variants = product.data?.variants ?? [];
    if (variants.length === 0) return null;
    if (variants.length === 1) return variants[0];

    const activeEntries = Object.entries(selectedOptions).filter(
      ([_, val]) => Boolean(val && val.trim()),
    );
    const requiredOptions = product.data?.options ?? [];
    if (activeEntries.length < requiredOptions.length) {
      return null;
    }

    return (
      variants.find((variant) =>
        variant.isActive !== false &&
        activeEntries.every(
          ([name, value]) => variant.optionValues[name] === value,
        ),
      ) ?? null
    );
  }, [product.data, selectedOptions]);

  /**
   * Kullanici seceneklerin sadece bir kismini (ornegin yalnizca Renk)
   * sectiginde bile galeride o renge ait varyantin gorselini gosterebilmek
   * icin uygun varyanti bulur.
   */
  const previewVariant = useMemo<ProductVariantDto | null>(() => {
    if (selectedVariant) return selectedVariant;
    const variants = product.data?.variants ?? [];
    if (variants.length === 0) return null;

    const activeEntries = Object.entries(selectedOptions).filter(
      ([_, val]) => Boolean(val && val.trim()),
    );
    if (activeEntries.length === 0) return null;

    return (
      variants.find(
        (v) =>
          v.isActive !== false &&
          v.imageUrl &&
          activeEntries.every(([name, val]) => v.optionValues[name] === val),
      ) ??
      variants.find(
        (v) =>
          v.isActive !== false &&
          activeEntries.every(([name, val]) => v.optionValues[name] === val),
      ) ??
      null
    );
  }, [selectedVariant, product.data, selectedOptions]);

  const { data: wishlistIds = [] } = useQuery({
    queryKey: ['account', 'wishlist-ids'],
    queryFn: () => accountApi.wishlistIds(),
    enabled: isAuthenticated,
    staleTime: 30_000,
  });

  const isFavorited = productId ? wishlistIds.includes(productId) : false;

  const toggleWishlist = useMutation({
    mutationFn: () => accountApi.toggleWishlist(productId!, selectedVariant?.id ?? null),
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey: ['account', 'wishlist-ids'] });
      void queryClient.invalidateQueries({ queryKey: ['account', 'wishlist'] });
      toast.success(res.message);
    },
    onError: (error) => toast.fromError(error, 'Favori işlemi gerçekleştirilemedi.'),
  });

  const handleFavorite = () => {
    if (!isAuthenticated) {
      toast.info('Favorilere eklemek için lütfen giriş yapın.');
      navigate(`/giris?redirect=${encodeURIComponent(location.pathname + location.search)}`);
      return;
    }
    toggleWishlist.mutate();
  };

  const restockMutation = useMutation({
    mutationFn: async () => {
      if (!isFavorited) {
        await accountApi.addToWishlist(productId!, selectedVariant?.id ?? null);
      }
      await accountApi.updateWishlistNotifications(productId!, true, undefined);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['account', 'wishlist-ids'] });
      void queryClient.invalidateQueries({ queryKey: ['account', 'wishlist'] });
      toast.success('Stok bildirimi başarıyla açıldı. Ürün stoğa girdiğinde e-posta ile haberdar edileceksiniz.');
    },
    onError: (err) => toast.fromError(err, 'Bildirim ayarlanamadı.'),
  });

  if (product.isLoading) return <PageLoader />;
  if (product.isError) {
    return (
      <div className="container-page py-12">
        <ErrorState error={product.error} onRetry={() => void product.refetch()} />
      </div>
    );
  }
  if (!product.data) return null;

  const item = product.data;

  /**
   * Bir secenek degerini secer veya zaten seciliyse kaldirir (toggle).
   * Bos dize atamak yerine anahtari nesneden tamamen siler.
   */
  const toggleOption = (optionName: string, value: string) => {
    setSelectedOptions((current) => {
      const next = { ...current };
      if (next[optionName] === value) {
        delete next[optionName];
      } else {
        next[optionName] = value;
      }
      return next;
    });
  };

  /**
   * Diger secili opsiyonlarla birlikte bu degerin secilebilir bir
   * varyanti olup olmadigini kontrol eder.
   */
  const isOptionValueReachable = (optionName: string, value: string) => {
    if (!item.variants || item.variants.length === 0) return true;

    return item.variants.some((variant) => {
      if (variant.isActive === false) return false;
      if (variant.optionValues[optionName] !== value) return false;

      return Object.entries(selectedOptions).every(([name, selected]) => {
        if (!selected || !selected.trim() || name === optionName) return true;
        return variant.optionValues[name] === selected;
      });
    });
  };

  const currentImage = previewVariant?.imageUrl ?? selectedVariant?.imageUrl ?? item.media[0]?.url ?? null;

  const maxQuantity = Math.min(
    selectedVariant?.stockQuantity ?? item.maximumOrderQuantity,
    item.maximumOrderQuantity || 99,
  );

  const canAdd =
    selectedVariant !== null && selectedVariant.isPurchasable && selectedVariant.isInStock;

  const onAdd = async () => {
    if (!selectedVariant) {
      toast.info('Lütfen seçenekleri belirleyin.');
      return;
    }
    await cart.addItem({
      productVariantId: selectedVariant.id,
      quantity,
      isGiftWrapped: false,
    });
    toast.success('Ürün sepete eklendi.');
  };

  const effectiveReviewCount = summary.data ? summary.data.totalCount : item.reviewCount;
  const effectiveRating = summary.data ? summary.data.averageRating : item.averageRating;

  return (
    <div className="container-page py-8">
      <Seo
        title={item.seoTitle ?? item.title}
        description={item.seoDescription ?? item.shortDescription}
        keywords={item.seoKeywords ?? item.tags}
        canonical={item.canonicalUrl}
        image={currentImage ?? item.media[0]?.url}
        type="product"
        brand={item.brandName}
        sku={selectedVariant?.sku ?? item.variants[0]?.sku}
        price={
          selectedVariant
            ? { amount: selectedVariant.price, currency: selectedVariant.currencyCode }
            : { amount: item.minPrice, currency: item.currencyCode }
        }
        inStock={selectedVariant ? selectedVariant.isInStock : item.variants.some((v) => v.isInStock)}
        rating={{ average: effectiveRating, count: effectiveReviewCount }}
        reviews={reviews.data?.items.map((r) => ({
          author: r.authorName || 'Müşteri',
          rating: r.rating,
          comment: r.comment,
          title: r.title,
          createdAt: r.createdAt,
        }))}
        breadcrumbs={[
          { name: 'Ana sayfa', url: '/' },
          ...item.breadcrumbs.map((c) => ({ name: c.name, url: `/kategori/${c.slug}` })),
          { name: item.title, url: `/urun/${item.slug}` },
        ]}
      />
      <nav aria-label="Kırılım" className="mb-6 flex flex-wrap items-center gap-1.5 text-sm text-ink-500">
        <Link to="/" className="hover:text-ink-800">
          Ana sayfa
        </Link>
        {item.breadcrumbs.map((crumb) => (
          <span key={crumb.id} className="flex items-center gap-1.5">
            <span aria-hidden="true">/</span>
            <Link to={`/kategori/${crumb.slug}`} className="hover:text-ink-800">
              {crumb.name}
            </Link>
          </span>
        ))}
        <span aria-hidden="true">/</span>
        <span className="text-ink-700">{item.title}</span>
      </nav>

      <div className="grid gap-10 lg:grid-cols-2">
        {/* Görseller: İnteraktif Zoom, Lightbox ve Küçük Resimler */}
        <div>
          <ProductImageGallery
            media={item.media}
            productTitle={item.title}
            selectedVariantImageUrl={previewVariant?.imageUrl ?? selectedVariant?.imageUrl}
          />
        </div>

        {/* Bilgi ve satin alma */}
        <div>
          {item.brandName ? (
            <Link to={`/marka/${item.brandSlug}`} className="text-sm uppercase tracking-wide text-ink-500 hover:text-ink-800">
              {item.brandName}
            </Link>
          ) : null}

          <h1 className="mt-1 text-2xl font-semibold tracking-tight text-ink-900">{item.title}</h1>

          {effectiveReviewCount > 0 ? (
            <div className="mt-2 flex items-center gap-2 text-sm text-ink-600">
              <Stars value={effectiveRating} className="h-4 w-4" />
              <span>{effectiveRating.toFixed(1)}</span>
              <a href="#yorumlar" className="link">
                {effectiveReviewCount} değerlendirme
              </a>
            </div>
          ) : null}

          <div className="mt-5 flex flex-wrap items-baseline gap-3">
            <span className="text-3xl font-semibold text-ink-900">
              {money(selectedVariant?.price ?? item.minPrice, item.currencyCode)}
            </span>
            {selectedVariant?.compareAtPrice && selectedVariant.compareAtPrice > selectedVariant.price ? (
              <span className="text-lg text-ink-400 line-through">
                {money(selectedVariant.compareAtPrice, item.currencyCode)}
              </span>
            ) : null}
          </div>

          {item.shortDescription ? (
            <p className="mt-4 text-sm leading-6 text-ink-600">{item.shortDescription}</p>
          ) : null}

          {/* Secenekler */}
          {item.options.length > 0 ? (
            <div className="mt-6 space-y-5">
              {item.options.map((option) => {
                const isSwatch =
                  option.displayType === 'swatch' ||
                  option.name.toLowerCase().includes('renk') ||
                  option.name.toLowerCase().includes('color');
                const isDropdown = !isSwatch && option.displayType === 'dropdown';

                // 1. Açılır Liste (Dropdown)
                if (isDropdown) {
                  return (
                    <div key={option.id}>
                      <label
                        htmlFor={`option-${option.id}`}
                        className="mb-2 block text-sm font-medium text-ink-800"
                      >
                        {option.name}
                        {selectedOptions[option.name] ? (
                          <span className="ml-1.5 font-normal text-ink-500">
                            : {cleanColorLabel(selectedOptions[option.name])}
                          </span>
                        ) : null}
                      </label>
                      <select
                        id={`option-${option.id}`}
                        value={selectedOptions[option.name] ?? ''}
                        onChange={(event) => {
                          const val = event.target.value;
                          setSelectedOptions((current) => {
                            const next = { ...current };
                            if (val) {
                              next[option.name] = val;
                            } else {
                              delete next[option.name];
                            }
                            return next;
                          });
                        }}
                        className="w-full max-w-xs rounded-lg border border-ink-300 bg-white px-3.5 py-2.5 text-sm font-medium text-ink-900 shadow-sm transition hover:border-ink-400 focus:border-ink-900 focus:outline-none focus:ring-1 focus:ring-ink-900"
                      >
                        <option value="">{option.name} seçiniz...</option>
                        {option.values.map((value) => {
                          const reachable = isOptionValueReachable(option.name, value.value);
                          const cleanName = cleanColorLabel(value.value);

                          return (
                            <option key={value.id} value={value.value} disabled={!reachable}>
                              {cleanName} {!reachable ? '(Tükendi)' : ''}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  );
                }

                // 2. Renk Kutusu (Swatch - Yuvarlak Top)
                if (isSwatch) {
                  return (
                    <div key={option.id}>
                      <div className="mb-2.5 flex items-center justify-between text-sm">
                        <span className="font-medium text-ink-800">{option.name}</span>
                        {selectedOptions[option.name] ? (
                          <span className="font-semibold text-ink-900">
                            {cleanColorLabel(selectedOptions[option.name])}
                          </span>
                        ) : (
                          <span className="text-xs text-ink-400">Renk seçiniz</span>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-3">
                        {option.values.map((value) => {
                          const active = selectedOptions[option.name] === value.value;
                          const reachable = isOptionValueReachable(option.name, value.value);
                          const swatchColor = resolveColor(value.value, value.colorCode);
                          const cleanName = cleanColorLabel(value.value);
                          const light = isLightColor(swatchColor);

                          return (
                            <button
                              key={value.id}
                              type="button"
                              disabled={!reachable}
                              onClick={() => toggleOption(option.name, value.value)}
                              title={cleanName}
                              aria-label={`${option.name}: ${cleanName}`}
                              className={`group relative flex h-10 w-10 items-center justify-center rounded-full transition-all focus:outline-none ${
                                active
                                  ? 'ring-2 ring-ink-900 ring-offset-2 scale-105 shadow-sm'
                                  : reachable
                                    ? 'hover:scale-110 hover:ring-2 hover:ring-ink-300 hover:ring-offset-1'
                                    : 'cursor-not-allowed opacity-35'
                              }`}
                            >
                              <span
                                className="h-8 w-8 rounded-full border border-black/15 shadow-inner transition"
                                style={{ backgroundColor: swatchColor }}
                                aria-hidden="true"
                              />
                              {active ? (
                                <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
                                  <Check
                                    className={`h-4 w-4 stroke-[3] ${
                                      light ? 'text-ink-900' : 'text-white'
                                    }`}
                                  />
                                </span>
                              ) : null}
                              {!reachable ? (
                                <span className="pointer-events-none absolute inset-0 m-auto h-0.5 w-7 rotate-45 bg-red-500 shadow-sm" />
                              ) : null}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                }

                // 3. Varsayılan: Düğme (Button)
                return (
                  <div key={option.id}>
                    <p className="mb-2 text-sm font-medium text-ink-800">
                      {option.name}
                      {selectedOptions[option.name] ? (
                        <span className="ml-1.5 font-normal text-ink-500">
                          : {cleanColorLabel(selectedOptions[option.name])}
                        </span>
                      ) : null}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {option.values.map((value) => {
                        const active = selectedOptions[option.name] === value.value;
                        const reachable = isOptionValueReachable(option.name, value.value);
                        const cleanName = cleanColorLabel(value.value);

                        return (
                          <button
                            key={value.id}
                            type="button"
                            disabled={!reachable}
                            onClick={() => toggleOption(option.name, value.value)}
                            className={`rounded-lg border px-3.5 py-2 text-sm font-medium transition ${
                              active
                                ? 'border-ink-900 bg-ink-900 text-white shadow-sm'
                                : reachable
                                  ? 'border-ink-200 bg-white text-ink-700 hover:border-ink-400 hover:bg-ink-50'
                                  : 'cursor-not-allowed border-ink-100 bg-ink-50 text-ink-300 line-through'
                            }`}
                          >
                            {cleanName}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : null}

          {/* Stok durumu */}
          <div className="mt-6">
            {selectedVariant === null ? (
              <p className="text-sm text-ink-500">Fiyat ve stok için seçenekleri belirleyin.</p>
            ) : !selectedVariant.isInStock ? (
              <p className="text-sm font-medium text-red-600">Bu seçenek tükendi.</p>
            ) : selectedVariant.isLowStock ? (
              <p className="text-sm font-medium text-amber-600">
                Son {selectedVariant.stockQuantity} adet
              </p>
            ) : (
              <p className="text-sm font-medium text-emerald-700">Stokta</p>
            )}
            {selectedVariant ? (
              <p className="mt-1 text-xs text-ink-400">Stok kodu: {selectedVariant.sku}</p>
            ) : null}
          </div>

          {/* Sepete ekle */}
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <div className="flex items-center rounded-lg border border-ink-200">
              <button
                type="button"
                className="px-3 py-2.5 text-ink-600 disabled:opacity-40"
                onClick={() => setQuantity((value) => Math.max(1, value - 1))}
                disabled={quantity <= 1}
                aria-label="Adedi azalt"
              >
                −
              </button>
              <input
                type="number"
                min={1}
                max={maxQuantity || 99}
                value={quantity}
                onChange={(event) =>
                  setQuantity(Math.max(1, Math.min(Number(event.target.value) || 1, maxQuantity || 99)))
                }
                className="w-14 border-0 text-center text-sm focus:outline-none"
                aria-label="Adet"
              />
              <button
                type="button"
                className="px-3 py-2.5 text-ink-600 disabled:opacity-40"
                onClick={() => setQuantity((value) => Math.min(maxQuantity || 99, value + 1))}
                disabled={quantity >= (maxQuantity || 99)}
                aria-label="Adedi artır"
              >
                +
              </button>
            </div>

            <button
              type="button"
              className="btn-primary flex-1 sm:flex-none sm:px-8"
              disabled={!canAdd || cart.busy}
              onClick={() => void onAdd()}
            >
              {cart.busy ? <Spinner className="h-4 w-4" /> : null}
              Sepete ekle
            </button>

            {(!canAdd && selectedVariant && !selectedVariant.isInStock) ? (
              <button
                type="button"
                onClick={() => {
                  if (!isAuthenticated) {
                    toast.info('Stok bildirimi almak için lütfen giriş yapın.');
                    navigate(`/giris?redirect=${encodeURIComponent(location.pathname + location.search)}`);
                    return;
                  }
                  restockMutation.mutate();
                }}
                disabled={restockMutation.isPending}
                className="btn-secondary flex items-center gap-2 border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100"
              >
                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                  <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                </svg>
                {restockMutation.isPending ? 'Kaydediliyor...' : 'Gelince Haber Ver'}
              </button>
            ) : null}

            <button
              type="button"
              className={`btn-secondary flex items-center gap-2 transition ${
                isFavorited ? 'border-rose-300 bg-rose-50 text-rose-600 hover:bg-rose-100' : ''
              }`}
              onClick={handleFavorite}
              disabled={toggleWishlist.isPending}
              title={isFavorited ? 'Favorilerden çıkar' : 'Favorilere ekle'}
            >
              <svg
                viewBox="0 0 24 24"
                className="h-4 w-4"
                fill={isFavorited ? 'currentColor' : 'none'}
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              {isFavorited ? 'Favorilerden çıkar' : 'Favorilere ekle'}
            </button>
          </div>

          {item.isPreOrder ? (
            <p className="mt-4 rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-800">
              Bu ürün ön siparişte.
              {item.preOrderReleaseDate
                ? ` Tahmini gönderim: ${formatDate(item.preOrderReleaseDate)}.`
                : ''}
            </p>
          ) : null}

          {item.minimumOrderQuantity > 1 ? (
            <p className="mt-3 text-xs text-ink-500">
              Bu üründen en az {item.minimumOrderQuantity} adet sipariş verilebilir.
            </p>
          ) : null}
        </div>
      </div>

      {/* Aciklama ve ozellikler */}
      <div className="mt-14 grid gap-10 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <h2 className="mb-3 text-lg font-semibold text-ink-900">Ürün açıklaması</h2>
          {item.description ? (
            // Icerik sunucuda HtmlSanitizer'dan gecirilerek saklanir.
            <div
              className="prose-content"
              // Icerik sunucuda HtmlSanitizer'dan gecirilerek saklanir; istemci tarafindan da temizlenir.
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.description) }}
            />
          ) : (
            <p className="text-sm text-ink-500">Bu ürün için açıklama girilmemiş.</p>
          )}
        </div>

        {item.specifications.length > 0 ? (
          <div>
            <h2 className="mb-3 text-lg font-semibold text-ink-900">Özellikler</h2>
            <dl className="divide-y divide-ink-100 rounded-xl border border-ink-200">
              {item.specifications.map((spec) => (
                <div key={spec.id} className="flex justify-between gap-4 px-4 py-2.5 text-sm">
                  <dt className="text-ink-500">{spec.label ?? spec.key}</dt>
                  <dd className="text-right font-medium text-ink-800">{spec.value}</dd>
                </div>
              ))}
            </dl>
          </div>
        ) : null}
      </div>

      {/* Yorumlar */}
      <section id="yorumlar" className="mt-14 scroll-mt-24">
        <h2 className="mb-5 text-lg font-semibold text-ink-900">Değerlendirmeler</h2>

        <div className="grid gap-8 lg:grid-cols-3">
          <div>
            {summary.data ? (
              <div className="card p-5">
                <p className="text-3xl font-semibold text-ink-900">
                  {summary.data.averageRating.toFixed(1)}
                </p>
                <Stars value={summary.data.averageRating} className="mt-1 h-4 w-4" />
                <p className="mt-1 text-sm text-ink-500">{summary.data.totalCount} değerlendirme</p>

                <div className="mt-4 space-y-1.5">
                  {[5, 4, 3, 2, 1].map((star) => {
                    const count = summary.data!.ratingBreakdown[star] ?? 0;
                    const total = summary.data!.totalCount || 1;
                    return (
                      <div key={star} className="flex items-center gap-2 text-xs text-ink-500">
                        <span className="w-3">{star}</span>
                        <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-ink-100">
                          <div
                            className="h-full bg-amber-400"
                            style={{ width: `${(count / total) * 100}%` }}
                          />
                        </div>
                        <span className="w-6 text-right">{count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : null}

            {isAuthenticated && productId ? (
              <ReviewForm productId={productId} />
            ) : (
              <p className="mt-4 text-sm text-ink-500">
                Değerlendirme yazmak için{' '}
                <Link to="/giris" className="link">
                  giriş yapın
                </Link>
                .
              </p>
            )}
          </div>

          <div className="lg:col-span-2">
            {reviews.isLoading ? (
              <PageLoader label="Yorumlar yükleniyor…" />
            ) : reviews.data && reviews.data.items.length > 0 ? (
              <>
                <ul className="space-y-5">
                  {reviews.data.items.map((review) => (
                    <li key={review.id} className="card p-5">
                      <div className="flex flex-wrap items-center gap-2">
                        <Stars value={review.rating} className="h-4 w-4" />
                        <span className="text-sm font-medium text-ink-800">
                          {review.authorName ?? 'Müşteri'}
                        </span>
                        {review.isVerifiedPurchase ? (
                          <span className="badge bg-emerald-50 text-emerald-700">Doğrulanmış alışveriş</span>
                        ) : null}
                        <span className="ml-auto text-xs text-ink-400">
                          {formatDate(review.createdAt)}
                        </span>
                      </div>

                      {review.title ? (
                        <h3 className="mt-2 text-sm font-semibold text-ink-900">{review.title}</h3>
                      ) : null}
                      {review.comment ? (
                        <p className="mt-1.5 text-sm leading-6 text-ink-600">{review.comment}</p>
                      ) : null}

                      {review.storeReply ? (
                        <div className="mt-3 rounded-lg bg-ink-50 px-4 py-3">
                          <p className="text-xs font-medium text-ink-700">Mağaza yanıtı</p>
                          <p className="mt-1 text-sm text-ink-600">{review.storeReply}</p>
                        </div>
                      ) : null}

                      <div className="mt-3.5 flex flex-wrap items-center gap-2 pt-2.5 border-t border-ink-100">
                        <span className="text-[11px] text-ink-400 mr-1">Bu değerlendirme faydalı oldu mu?</span>
                        <button
                          type="button"
                          disabled={votingReviewId === review.id}
                          onClick={() => void handleVote(review.id, true)}
                          className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs font-medium transition-all ${
                            review.currentUserVote === true
                              ? 'border-emerald-300 bg-emerald-50 text-emerald-700 shadow-sm ring-1 ring-emerald-400/30'
                              : 'border-ink-200 bg-white text-ink-600 hover:border-ink-300 hover:bg-ink-50 hover:text-ink-900'
                          }`}
                          title={review.currentUserVote === true ? 'Oyu geri al' : 'Faydalı buldum'}
                        >
                          <ThumbsUp
                            className={`h-3.5 w-3.5 ${
                              review.currentUserVote === true ? 'fill-emerald-600 text-emerald-600' : 'text-ink-400'
                            }`}
                          />
                          <span>Faydalı ({review.helpfulCount})</span>
                        </button>
                        <button
                          type="button"
                          disabled={votingReviewId === review.id}
                          onClick={() => void handleVote(review.id, false)}
                          className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs font-medium transition-all ${
                            review.currentUserVote === false
                              ? 'border-rose-300 bg-rose-50 text-rose-700 shadow-sm ring-1 ring-rose-400/30'
                              : 'border-ink-200 bg-white text-ink-600 hover:border-ink-300 hover:bg-ink-50 hover:text-ink-900'
                          }`}
                          title={review.currentUserVote === false ? 'Oyu geri al' : 'Faydalı değil'}
                        >
                          <ThumbsDown
                            className={`h-3.5 w-3.5 ${
                              review.currentUserVote === false ? 'fill-rose-600 text-rose-600' : 'text-ink-400'
                            }`}
                          />
                          <span>Faydalı değil ({review.notHelpfulCount ?? 0})</span>
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>

                <Pagination
                  page={reviews.data.page}
                  totalPages={reviews.data.totalPages}
                  totalCount={reviews.data.totalCount}
                  onChange={setReviewPage}
                />
              </>
            ) : (
              <EmptyState
                title="Henüz değerlendirme yok"
                description="Bu ürünü ilk değerlendiren siz olun."
              />
            )}
          </div>
        </div>
      </section>

      {/* Benzer urunler */}
      {related.data && related.data.length > 0 ? (
        <section className="mt-14">
          <h2 className="mb-5 text-lg font-semibold text-ink-900">Benzer ürünler</h2>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            {related.data.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}

function ReviewForm({ productId }: { productId: number }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<ProductReviewCreateDto>({
    productId,
    rating: 5,
    title: '',
    comment: '',
    imageUrls: [],
  });

  const eligibility = useQuery({
    queryKey: ['product', productId, 'review-eligibility'],
    queryFn: () => catalogApi.checkReviewEligibility(productId),
  });

  const mutation = useMutation({
    mutationFn: () =>
      catalogApi.createReview({
        ...form,
        productId,
        orderNumber: eligibility.data?.orderNumber ?? undefined,
      }),
    onSuccess: () => {
      toast.success('Değerlendirmeniz alındı.', 'Onaylandıktan sonra yayınlanacak.');
      setForm({ productId, rating: 5, title: '', comment: '', imageUrls: [] });
      void queryClient.invalidateQueries({ queryKey: ['product', productId, 'reviews'] });
      void queryClient.invalidateQueries({ queryKey: ['product', productId, 'review-eligibility'] });
    },
    onError: (error) => toast.fromError(error, 'Değerlendirme gönderilemedi.'),
  });

  if (eligibility.isLoading) {
    return (
      <div className="card mt-4 p-6 flex justify-center">
        <Spinner className="h-5 w-5 text-ink-400" />
      </div>
    );
  }

  if (eligibility.data?.alreadyReviewed) {
    return (
      <div className="card mt-4 p-5 bg-emerald-50/50 border-emerald-200">
        <div className="flex items-center gap-2 text-emerald-800 font-medium text-sm">
          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">✓</span>
          Bu ürünü daha önce değerlendirdiniz
        </div>
        <p className="mt-1 text-xs text-emerald-700">Değerlendirmeniz ve geri bildiriminiz için teşekkür ederiz!</p>
      </div>
    );
  }

  if (eligibility.data && !eligibility.data.canReview) {
    return (
      <div className="card mt-4 p-5 bg-amber-50/60 border-amber-200">
        <p className="text-sm font-semibold text-amber-900">Değerlendirme Bilgisi</p>
        <p className="mt-1 text-xs text-amber-700 leading-relaxed">
          {eligibility.data.reason ?? 'Yalnızca bu ürünü satın alıp teslim almış müşteriler değerlendirme yapabilir.'}
        </p>
      </div>
    );
  }

  return (
    <form
      className="card mt-4 space-y-3 p-5"
      onSubmit={(event) => {
        event.preventDefault();
        mutation.mutate();
      }}
    >
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-ink-900">Değerlendirme yaz</h3>
        {eligibility.data?.hasPurchased ? (
          <span className="badge bg-emerald-50 text-emerald-700 text-xs">
            ✓ Doğrulanmış Alıcı
          </span>
        ) : null}
      </div>

      <FormErrors error={mutation.error} />

      <Field label="Puan" htmlFor="review-rating" required>
        <div className="flex gap-1">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type="button"
              onClick={() => setForm((current) => ({ ...current, rating: star }))}
              aria-label={`${star} yıldız`}
              className="p-0.5"
            >
              <svg
                viewBox="0 0 20 20"
                className={`h-6 w-6 ${star <= form.rating ? 'text-amber-400' : 'text-ink-200'}`}
                fill="currentColor"
              >
                <path d="M10 1.8l2.4 4.9 5.4.8-3.9 3.8.9 5.4L10 14.1l-4.8 2.6.9-5.4L2.2 7.5l5.4-.8L10 1.8z" />
              </svg>
            </button>
          ))}
        </div>
      </Field>

      <Field label="Başlık" htmlFor="review-title">
        <input
          id="review-title"
          className="field"
          maxLength={150}
          value={form.title ?? ''}
          onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
        />
      </Field>

      <Field label="Yorumunuz" htmlFor="review-comment" required>
        <textarea
          id="review-comment"
          className="field min-h-24"
          required
          maxLength={4000}
          value={form.comment}
          onChange={(event) => setForm((current) => ({ ...current, comment: event.target.value }))}
        />
      </Field>

      <button type="submit" className="btn-primary w-full" disabled={mutation.isPending}>
        {mutation.isPending ? <Spinner className="h-4 w-4" /> : null}
        Gönder
      </button>
    </form>
  );
}
